/*
  Helpers for the Correlations tab

  Pure functions with no Vue/Vuex dependency, so they can be unit tested
  directly. Values come from baselineValuesBySubject() in ./dashboard, as a Map
  of subject_id -> raw value, so every correlation here is computed on one
  value per subject at baseline -- the same snapshot the Data Dashboard shows.
  Where a collection saves a first and last visit for a variable, it can also
  be read at its last visit or as its first-to-last change (see
  visitMeasures), each a variable of its own in the same Map shape.

  The heatmap's r is computed here so the matrix redraws instantly. The
  statistics in the pair detail (p, confidence interval, fitted line) come from
  /api/compute-correlation, built by buildCorrelationPayload().
*/
import { interpolateRdBu } from 'd3';
import { sortScales } from './helpers';
import {
  DATA_CATEGORY_CATEGORICAL,
  DATA_CATEGORY_CONTINUOUS,
  configuredFirstVisit,
  configuredLastVisit,
  isDashboardEligible,
  isPredictor,
  levelCounts,
} from './dashboard';

export const METHOD_PEARSON = 'pearson';
export const METHOD_SPEARMAN = 'spearman';

export const SORT_SELECTION = 'selection';
export const SORT_STRENGTH = 'strength';

// Fewer complete pairs than this and a cell is hatched rather than coloured.
// Matches MIN_CORRELATION_PAIRS in the compute-correlation endpoint.
export const MIN_PAIRS = 10;

// Cap on the default selection, for layout rather than cost: a 12 x 12 matrix
// still fits on screen with readable labels.
export const MAX_DEFAULT_VARIABLES = 12;

// A covariate column whose part not explained by the columns before it is
// smaller than this (on a unit-norm column) adds nothing and is skipped. Mirrors
// COVARIATE_RANK_TOLERANCE in the compute-correlation endpoint; exact
// collinearity, such as a dummy for a level absent from a stratum, leaves a
// remainder many orders of magnitude below either threshold.
const COVARIATE_TOLERANCE = 1e-9;

// Scatter subsampling, matching CohortManager/OutcomesAnalysis/
// ParallelCoordinates.vue: above SUBSAMPLE_MIN points it switches on by
// default, keeping every k-th point at one of these tiers. Only the drawing is
// thinned; statistics always use every subject.
export const SUBSAMPLE_MIN = 20000;
export const SUBSAMPLE_LABELS = ['1%', '5%', '10%', '20%', '50%'];
export const SUBSAMPLE_STRIDES = [100, 20, 10, 5, 2];

/**
 * Only continuous variables are correlated. Categorical pairs need different
 * measures (Cramer's V, point-biserial) and are out of scope for v1.
 */
export function isCorrelationEligible(v) {
  return isDashboardEligible(v) && v.data_category === DATA_CATEGORY_CONTINUOUS;
}

/**
 * Initial selection: continuous outcomes first, in sortScales() order, then
 * continuous predictors (age, BMI, ...) to fill up to the cap. Outcomes lead
 * because the wireframe's framing is outcome x outcome, but a dataset with few
 * continuous outcomes still gets a useful matrix.
 */
export function selectDefaultCorrelationVariables(variables, options = {}) {
  const { max = MAX_DEFAULT_VARIABLES } = options;
  const eligible = (variables || []).filter(isCorrelationEligible);
  const outcomes = sortScales(eligible.filter(v => !isPredictor(v)));
  const predictors = sortScales(eligible.filter(isPredictor));
  return outcomes.concat(predictors).slice(0, max);
}

// appended to a variable's uid to name its last-visit value and its change
export const LAST_SUFFIX = ':last';
export const DELTA_SUFFIX = ':delta';

/**
 * A variable read at each of its two saved visits and as the change between
 * them, each a variable of its own so the matrix, pair detail, and caches treat
 * it like any other. Names carry the visits, since each variable in a
 * collection can have its own. The first-visit measure keeps the variable's
 * uid, so selections and covariates still match it. Only call for variables
 * that pass hasVisitWindow().
 *
 * @param variable a dashboard variable, already carrying its `uid`
 * @returns {first, last, delta}
 */
export function visitMeasures(variable, visitVariable) {
  const firstVisit = configuredFirstVisit(variable, visitVariable);
  const lastVisit = configuredLastVisit(variable, visitVariable);
  const span = `${firstVisit}→${lastVisit}`;
  const shared = {
    ...variable,
    baseUid: variable.uid,
    baseAbbreviation: variable.abbreviation,
  };
  return {
    first: {
      ...shared,
      measure: 'first',
      abbreviation: `${variable.abbreviation} (${firstVisit})`,
      label: `${variable.label} (${firstVisit})`,
    },
    last: {
      ...shared,
      uid: variable.uid + LAST_SUFFIX,
      measure: 'last',
      abbreviation: `${variable.abbreviation} (${lastVisit})`,
      label: `${variable.label} (${lastVisit})`,
    },
    delta: {
      ...shared,
      uid: variable.uid + DELTA_SUFFIX,
      measure: 'delta',
      delta: true,
      window: span,
      abbreviation: `Δ ${variable.abbreviation} (${span})`,
      label: `Δ ${variable.label} (${span})`,
    },
  };
}

/**
 * The variables a matrix is built over: each variable in list order, as its
 * selected measures in First Visit, Last Visit, Change order. A variable
 * without saved visits has only its single value. Any measure can be chosen
 * without the others.
 *
 * @param available variables in list order
 * @param measuresByUid Map of uid -> visitMeasures(), for those that have them
 */
export function matrixVariables(
  available,
  { firstIds = [], lastIds = [], deltaIds = [] },
  measuresByUid
) {
  const first = new Set(firstIds);
  const last = new Set(lastIds);
  const deltas = new Set(deltaIds);
  const out = [];
  available.forEach(v => {
    const m = measuresByUid.get(v.uid);
    if (first.has(v.uid)) out.push(m ? m.first : v);
    if (!m) return;
    if (last.has(v.uid)) out.push(m.last);
    if (deltas.has(v.uid)) out.push(m.delta);
  });
  return out;
}

/**
 * Whether one measure is a change and the other is the same variable at one of
 * that change's own visits. The two share that visit's value, so regression to
 * the mean and measurement error alone pull their correlation away from zero
 * (negative with the first visit, positive with the last); without a
 * correction (e.g. Oldham's) the r is mostly that artifact, so the matrix
 * leaves these cells blank. First Visit x Last Visit is a real measure of
 * stability and is not coupled.
 */
export function isCoupledPair(a, b) {
  const root = v => v.baseUid || v.uid;
  return a.uid !== b.uid && root(a) === root(b) && (!!a.delta || !!b.delta);
}

/**
 * Blank the cells of a matrix whose pair is coupled (see isCoupledPair): r is
 * withheld and only n is kept, so they read like the diagonal and, having no
 * r, count toward no variable's strength.
 *
 * @param variables parallel to the rows and columns of cells
 */
export function markCoupled(cells, variables) {
  return cells.map((row, i) =>
    row.map((cell, j) =>
      i !== j && isCoupledPair(variables[i], variables[j])
        ? { r: null, n: cell.n, coupled: true }
        : cell
    )
  );
}

function toNumber(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Subjects holding a numeric value for both variables, as parallel arrays.
 *
 * @param subjectIds optional restriction, e.g. the members of one stratum
 * @returns {x, y, subjectIds}
 */
export function pairedValues(xValues, yValues, subjectIds = null) {
  const x = [];
  const y = [];
  const ids = [];
  const candidates = subjectIds || Array.from(xValues.keys());

  candidates.forEach(id => {
    const a = toNumber(xValues.get(id));
    if (a === null) return;
    const b = toNumber(yValues.get(id));
    if (b === null) return;
    x.push(a);
    y.push(b);
    ids.push(id);
  });

  return { x, y, subjectIds: ids };
}

/**
 * Subjects with a value for one of the two variables but not the other: the
 * "excluded" count in the pair detail.
 */
export function excludedCount(xValues, yValues) {
  const present = new Set();
  const both = pairedValues(xValues, yValues).subjectIds.length;
  [xValues, yValues].forEach(values => {
    values.forEach((v, id) => {
      if (toNumber(v) !== null) present.add(id);
    });
  });
  return present.size - both;
}

/**
 * Pearson's r, or null when it is undefined (fewer than two pairs, or either
 * variable constant).
 */
export function pearson(x, y) {
  const n = x.length;
  if (n < 2 || y.length !== n) return null;

  let sx = 0;
  let sy = 0;
  for (let i = 0; i < n; i += 1) {
    sx += x[i];
    sy += y[i];
  }
  const mx = sx / n;
  const my = sy / n;

  let sxy = 0;
  let sxx = 0;
  let syy = 0;
  for (let i = 0; i < n; i += 1) {
    const dx = x[i] - mx;
    const dy = y[i] - my;
    sxy += dx * dy;
    sxx += dx * dx;
    syy += dy * dy;
  }

  if (sxx === 0 || syy === 0) return null;
  // rounding can carry a perfect correlation fractionally past +/-1
  return Math.max(-1, Math.min(1, sxy / Math.sqrt(sxx * syy)));
}

/**
 * 1-based ranks, with tied values sharing the mean of the ranks they span --
 * the convention scipy's spearmanr uses.
 */
export function ranks(values) {
  const order = values.map((v, i) => i).sort((a, b) => values[a] - values[b]);
  const out = new Array(values.length);

  let i = 0;
  while (i < order.length) {
    let j = i;
    while (j + 1 < order.length && values[order[j + 1]] === values[order[i]]) {
      j += 1;
    }
    const rank = (i + j) / 2 + 1;
    for (let k = i; k <= j; k += 1) out[order[k]] = rank;
    i = j + 1;
  }

  return out;
}

/**
 * Spearman's rho: Pearson's r on the ranks. Ranking happens after pairing, so
 * each pair is ranked within its own complete cases.
 */
export function spearman(x, y) {
  if (x.length < 2 || y.length !== x.length) return null;
  return pearson(ranks(x), ranks(y));
}

/**
 * Whether a variable can be adjusted for: any predictor (subject variable or
 * Demographics observation), continuous or categorical.
 */
export function isCovariateEligible(v) {
  return isDashboardEligible(v) && isPredictor(v);
}

/**
 * The covariate shape the helpers below take, from a variable and its baseline
 * values.
 */
export function covariateSpec(variable, values) {
  return {
    values,
    categorical: variable.data_category === DATA_CATEGORY_CATEGORICAL,
  };
}

function hasCovariateValue(spec, id) {
  const v = spec.values.get(id);
  if (spec.categorical) return v != null && v !== '';
  return toNumber(v) !== null;
}

/**
 * Numeric covariate columns over the given subjects, every one of whom must
 * hold a value for every covariate. A categorical covariate becomes one 0/1
 * column per level except the most frequent, which is the reference. Levels
 * are counted among these subjects only, so a stratum gets its own coding.
 */
export function covariateColumns(subjectIds, covariates) {
  const columns = [];
  covariates.forEach(c => {
    if (!c.categorical) {
      columns.push(subjectIds.map(id => toNumber(c.values.get(id))));
      return;
    }
    const levels = levelCounts(subjectIds.map(id => c.values.get(id)));
    levels.slice(1).forEach(({ level }) => {
      columns.push(
        subjectIds.map(id => (String(c.values.get(id)) === level ? 1 : 0))
      );
    });
  });
  return columns;
}

/**
 * Complete pairs that also hold a value for every covariate, with the
 * covariate columns built over exactly those subjects.
 *
 * @param covariates [{values, categorical}] from covariateSpec()
 * @returns {x, y, subjectIds, z, dropped} -- z is a list of columns parallel
 *          to x; dropped counts the pairs lost to a missing covariate
 */
export function adjustedPairs(xValues, yValues, covariates, subjectIds = null) {
  const base = pairedValues(xValues, yValues, subjectIds);
  if (!covariates || covariates.length === 0) {
    return { ...base, z: [], dropped: 0 };
  }

  const x = [];
  const y = [];
  const ids = [];
  base.subjectIds.forEach((id, i) => {
    if (!covariates.every(c => hasCovariateValue(c, id))) return;
    x.push(base.x[i]);
    y.push(base.y[i]);
    ids.push(id);
  });

  return {
    x,
    y,
    subjectIds: ids,
    z: covariateColumns(ids, covariates),
    dropped: base.subjectIds.length - ids.length,
  };
}

function dot(a, b) {
  let s = 0;
  for (let i = 0; i < a.length; i += 1) s += a[i] * b[i];
  return s;
}

function centered(values) {
  const m = values.reduce((sum, v) => sum + v, 0) / (values.length || 1);
  return values.map(v => v - m);
}

/**
 * Orthonormal basis for the centred covariate columns, by modified
 * Gram-Schmidt with one re-orthogonalization pass. Constant and collinear
 * columns are skipped, so basis.length is the number of covariate degrees of
 * freedom actually used.
 */
export function orthonormalBasis(columns) {
  const basis = [];
  columns.forEach(col => {
    let v = centered(col);
    const norm0 = Math.sqrt(dot(v, v));
    if (norm0 === 0) return;
    v = v.map(a => a / norm0);

    for (let pass = 0; pass < 2; pass += 1) {
      basis.forEach(q => {
        const d = dot(v, q);
        for (let i = 0; i < v.length; i += 1) v[i] -= d * q[i];
      });
    }

    const norm = Math.sqrt(dot(v, v));
    if (norm < COVARIATE_TOLERANCE) return;
    basis.push(v.map(a => a / norm));
  });
  return basis;
}

/**
 * What is left of a variable once its mean and everything the basis explains
 * are removed.
 */
export function residuals(values, basis) {
  const r = centered(values);
  basis.forEach(q => {
    const d = dot(r, q);
    for (let i = 0; i < r.length; i += 1) r[i] -= d * q[i];
  });
  return r;
}

/**
 * Partial correlation of x and y given covariate columns z. Spearman ranks
 * everything first, covariates included; ranking a 0/1 dummy is an affine
 * change, so it spans the same space. Agrees with the endpoint's adjusted
 * statistics.
 *
 * @returns {r, k} -- k is the number of covariate columns that carried
 *          independent information
 */
export function partialCorrelation(x, y, z, method = METHOD_PEARSON) {
  const spearmanMethod = method === METHOD_SPEARMAN;
  const xs = spearmanMethod ? ranks(x) : x;
  const ys = spearmanMethod ? ranks(y) : y;
  const zs = spearmanMethod ? z.map(ranks) : z;

  const basis = orthonormalBasis(zs);
  return {
    r: pearson(residuals(xs, basis), residuals(ys, basis)),
    k: basis.length,
  };
}

/**
 * Adjusted values for the scatter: each variable's residual after the
 * covariates, shifted back by its mean so the axes stay in the original units.
 * The fitted line the endpoint returns for the adjusted view is built the same
 * way.
 */
export function adjustedPoints(paired) {
  const basis = orthonormalBasis(paired.z || []);
  const shift = (values, resid) => {
    const m = values.reduce((sum, v) => sum + v, 0) / (values.length || 1);
    return resid.map(r => r + m);
  };
  return {
    x: shift(paired.x, residuals(paired.x, basis)),
    y: shift(paired.y, residuals(paired.y, basis)),
  };
}

/**
 * One heatmap cell. r is withheld below MIN_PAIRS so a correlation over a
 * handful of subjects is never coloured as if it meant something.
 *
 * With covariates, r is the partial correlation and n counts the subjects who
 * also hold every covariate; rUnadjusted is the plain correlation on those same
 * subjects, so the two differ only by the adjustment.
 */
export function correlate(
  xValues,
  yValues,
  method = METHOD_PEARSON,
  covariates = []
) {
  const fn = method === METHOD_SPEARMAN ? spearman : pearson;

  if (covariates.length === 0) {
    const { x, y } = pairedValues(xValues, yValues);
    const n = x.length;
    if (n < MIN_PAIRS) return { r: null, n };
    return { r: fn(x, y), n };
  }

  const p = adjustedPairs(xValues, yValues, covariates);
  const n = p.x.length;
  if (n < MIN_PAIRS) return { r: null, n, rUnadjusted: null };

  const { r, k } = partialCorrelation(p.x, p.y, p.z, method);
  return {
    // a partial r needs residual degrees of freedom left over
    r: n - 2 - k >= 1 ? r : null,
    n,
    rUnadjusted: fn(p.x, p.y),
  };
}

/**
 * Full symmetric matrix over the given variables. Each pair is computed once
 * and mirrored.
 *
 * @param valuesList baseline Maps, parallel to the variables being correlated
 * @param options.covariates covariateSpec() list to adjust every cell for
 * @param options.covariateIndices indices of matrix variables that are also
 *        covariates: adjusting a variable for itself leaves nothing, so their
 *        off-diagonal cells are marked rather than computed
 * @returns cells[i][j] = {r, n, ...}; the diagonal holds r = 1 and the
 *          variable's n
 */
export function correlationMatrix(
  valuesList,
  method = METHOD_PEARSON,
  options = {}
) {
  const { covariates = [], covariateIndices = new Set() } = options;
  const k = valuesList.length;
  const cells = valuesList.map(() => new Array(k));

  for (let i = 0; i < k; i += 1) {
    let n = 0;
    valuesList[i].forEach(v => {
      if (toNumber(v) !== null) n += 1;
    });
    cells[i][i] = { r: 1, n, diagonal: true };

    for (let j = i + 1; j < k; j += 1) {
      const cell =
        covariateIndices.has(i) || covariateIndices.has(j)
          ? { r: null, n: 0, covariate: true }
          : correlate(valuesList[i], valuesList[j], method, covariates);
      cells[i][j] = cell;
      cells[j][i] = cell;
    }
  }

  return cells;
}

/**
 * Indices ordered by each variable's mean |r| with the others, strongest
 * first. Cells without an r do not count toward the mean; a variable with no
 * usable cell at all sinks to the bottom. Ties keep selection order.
 */
export function strengthOrder(cells) {
  const strength = cells.map((row, i) => {
    let sum = 0;
    let count = 0;
    row.forEach((c, j) => {
      if (i === j || !c || c.r === null) return;
      sum += Math.abs(c.r);
      count += 1;
    });
    return count === 0 ? -1 : sum / count;
  });

  return cells.map((row, i) => i).sort((a, b) => strength[b] - strength[a]);
}

/**
 * Reorder a matrix's rows and columns together.
 */
export function permuteMatrix(cells, order) {
  return order.map(i => order.map(j => cells[i][j]));
}

/**
 * Complete pairs within each stratum of a categorical variable.
 *
 * @param derived deriveGroups() result for the stratifying variable, so levels
 *        are capped and floored exactly as on the Data Dashboard
 * @param covariates covariateSpec() list to adjust for within each stratum.
 *        The caller leaves out the stratifying variable itself, which is
 *        constant inside a stratum; covariate columns are coded per stratum.
 * @returns [{level, x, y, subjectIds, z, dropped}] in derived.levels order
 */
export function stratifiedPairs(derived, xValues, yValues, covariates = []) {
  return derived.levels.map(({ level }) => ({
    level,
    ...adjustedPairs(
      xValues,
      yValues,
      covariates,
      derived.subjectsByLevel.get(level) || []
    ),
  }));
}

/**
 * Strata whose correlation points the other way from the overall one, the
 * signature of Simpson's paradox. A zero or missing r reverses nothing.
 *
 * @param strata [{level, r}]
 * @returns levels, in the order given
 */
export function signReversals(overallR, strata) {
  if (overallR == null || overallR === 0) return [];
  return strata
    .filter(
      s => s.r != null && s.r !== 0 && Math.sign(s.r) !== Math.sign(overallR)
    )
    .map(s => s.level);
}

/**
 * POST body for /api/compute-correlation. Only complete cases are sent; the
 * endpoint pairs x, y, and each covariate column in z by position.
 */
export function buildCorrelationPayload(paired, strata = []) {
  return {
    x: paired.x,
    y: paired.y,
    z: paired.z || [],
    strata: strata.map(s => ({
      level: s.level,
      x: s.x,
      y: s.y,
      z: s.z || [],
    })),
  };
}

/**
 * r to two decimals with a true minus sign, so a column of coefficients lines
 * up and a negative one is not mistaken for a hyphen.
 */
export function formatR(r) {
  if (r == null || !Number.isFinite(r)) return '—';
  const s = Math.abs(r).toFixed(2);
  if (s === '0.00') return s;
  return r < 0 ? `−${s}` : s;
}

/**
 * Diverging fill for a cell: red for negative, blue for positive, white at 0.
 * RdBu runs red -> blue over [0, 1], so r in [-1, 1] maps straight onto it.
 */
export function correlationColor(r) {
  if (r == null || !Number.isFinite(r)) return null;
  return interpolateRdBu((Math.max(-1, Math.min(1, r)) + 1) / 2);
}

/**
 * The darkest ends of RdBu need light text to stay legible.
 */
export function correlationTextColor(r) {
  return r != null && Math.abs(r) >= 0.6 ? '#ffffff' : '#333333';
}

/**
 * The default subsampling tier for n points, using the same thresholds as
 * ParallelCoordinates: the larger the scatter, the smaller the share drawn.
 *
 * @returns an index into SUBSAMPLE_LABELS / SUBSAMPLE_STRIDES, or null when n
 *          is small enough to draw in full
 */
export function defaultSubsampleChoice(n, min = SUBSAMPLE_MIN) {
  if (!(n > min)) return null;
  if (n >= min * 100) return 0;
  if (n >= min * 20) return 1;
  if (n >= min * 10) return 2;
  if (n >= min * 5) return 3;
  return 4;
}

/**
 * Every stride-th point of a paired {x, y}, as ParallelCoordinates does.
 * Deterministic, so the same pair always draws the same subjects.
 */
export function subsamplePoints(points, stride) {
  if (!(stride > 1)) return { x: points.x, y: points.y };
  const x = [];
  const y = [];
  for (let i = 0; i < points.x.length; i += stride) {
    x.push(points.x[i]);
    y.push(points.y[i]);
  }
  return { x, y };
}

/**
 * Whether an axis can be drawn on a log scale. Plotly silently drops values
 * at or below zero on a log axis, which would hide subjects without saying so.
 */
export function allPositive(values) {
  return values.length > 0 && values.every(v => v > 0);
}

/**
 * Default export filename for a pair's scatter, following the podvis_d{id}_...
 * prefix used by the other exports (Audiogram, SummaryStatsTable). Studies have
 * no existing convention, so they take `s` in place of `d`.
 *
 * @param scope 'collection' or 'study'
 */
export function pairExportFilename(scope, id, xName, yName, stratifyName) {
  const clean = name =>
    String(name)
      .replace(/[^A-Za-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  const prefix = scope === 'collection' ? 'd' : 's';
  let filename = `podvis_${prefix}${id}_correlation_${clean(yName)}_vs_${clean(
    xName
  )}`;
  if (stratifyName) filename += `_by_${clean(stratifyName)}`;
  return filename;
}
